import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { Skeleton } from '@admin-panel/ui';
import {
  useGetOccasionsQuery,
  useGetLookGroupsInfiniteQuery,
} from '../../store/stylist/stylistApi';
import LookGroupWorkspace from './components/LookGroupWorkspace';
import LookGroupsToolbar from './components/LookGroupsToolbar';

const FILTER_FEEDBACK_MIN_DURATION_MS = 320;
const getLookGroupsFilterKey = (occasionId: number | null, subOccasionIds: Array<number>) =>
  `${occasionId ?? ''}:${subOccasionIds[0] ?? ''}`;
const getFirstValidSubOccasionId = (subOccasionIds: Array<number>) =>
  subOccasionIds.find(id => Number.isInteger(id) && id > 0) ?? null;

const LookGroups = () => {
  const [selectedOccasionId, setSelectedOccasionId] = useState<number | null>(null);
  const [selectedSubOccasionIds, setSelectedSubOccasionIds] = useState<Array<number>>([]);
  const [isFilterPending, setIsFilterPending] = useState(false);
  const pendingFilterKeyRef = useRef<string | null>(null);
  const filterPendingStartedAtRef = useRef<number | null>(null);
  const filterPendingTimeoutRef = useRef<number | null>(null);
  const isLoadingNextPageRef = useRef(false);
  const lastTriggeredGroupIdRef = useRef<number | null>(null);
  const { data: occasionTree = [] } = useGetOccasionsQuery();

  const selectedSubOccasionId = useMemo(
    () => getFirstValidSubOccasionId(selectedSubOccasionIds),
    [selectedSubOccasionIds],
  );

  const lookGroupsQueryArg = useMemo(
    () => ({
      enrich_looks: true,
      ...(typeof selectedOccasionId === 'number' ? { occasion: selectedOccasionId } : {}),
      ...(typeof selectedSubOccasionId === 'number' ? { sub_occasion: selectedSubOccasionId } : {}),
    }),
    [selectedOccasionId, selectedSubOccasionId],
  );

  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetLookGroupsInfiniteQuery(lookGroupsQueryArg);

  const activeFilterKey = useMemo(
    () => getLookGroupsFilterKey(selectedOccasionId, selectedSubOccasionIds),
    [selectedOccasionId, selectedSubOccasionIds],
  );

  const lookGroups = useMemo(() => data?.pages.flatMap(page => page) ?? [], [data]);
  const lastVisibleGroupId = lookGroups[lookGroups.length - 1]?.id ?? null;

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (
      isFilterPending ||
      !hasNextPage ||
      isLoadingNextPageRef.current ||
      isFetchingNextPage ||
      !lastVisibleGroupId ||
      lastTriggeredGroupIdRef.current === lastVisibleGroupId
    ) {
      return;
    }

    lastTriggeredGroupIdRef.current = lastVisibleGroupId;
    isLoadingNextPageRef.current = true;
    void fetchNextPage().finally(() => {
      isLoadingNextPageRef.current = false;
    });
  });

  useEffect(() => {
    return () => {
      if (filterPendingTimeoutRef.current !== null) {
        window.clearTimeout(filterPendingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    lastTriggeredGroupIdRef.current = null;
    isLoadingNextPageRef.current = false;
  }, [lookGroupsQueryArg]);

  useEffect(() => {
    if (
      isFilterPending &&
      pendingFilterKeyRef.current === activeFilterKey &&
      !isLoading &&
      !isFetchingNextPage &&
      (!isFetching || Boolean(data) || isError)
    ) {
      const elapsed = Date.now() - (filterPendingStartedAtRef.current ?? Date.now());
      const clearPendingState = () => {
        setIsFilterPending(false);
        pendingFilterKeyRef.current = null;
        filterPendingStartedAtRef.current = null;
        filterPendingTimeoutRef.current = null;
      };

      if (elapsed >= FILTER_FEEDBACK_MIN_DURATION_MS) {
        clearPendingState();
      } else if (filterPendingTimeoutRef.current === null) {
        filterPendingTimeoutRef.current = window.setTimeout(
          clearPendingState,
          FILTER_FEEDBACK_MIN_DURATION_MS - elapsed,
        );
      }
    }
  }, [activeFilterKey, data, isError, isFetching, isFetchingNextPage, isFilterPending, isLoading]);

  const handleOccasionFilterChange = useCallback(
    ({
      occasionId,
      subOccasionIds,
    }: {
      occasionId: number | null;
      subOccasionIds: Array<number>;
    }) => {
      const hasChanged =
        occasionId !== selectedOccasionId ||
        subOccasionIds.length !== selectedSubOccasionIds.length ||
        subOccasionIds.some((id, index) => id !== selectedSubOccasionIds[index]);

      if (!hasChanged) {
        return;
      }

      pendingFilterKeyRef.current = getLookGroupsFilterKey(occasionId, subOccasionIds);
      filterPendingStartedAtRef.current = Date.now();
      if (filterPendingTimeoutRef.current !== null) {
        window.clearTimeout(filterPendingTimeoutRef.current);
        filterPendingTimeoutRef.current = null;
      }
      setIsFilterPending(true);
      setSelectedOccasionId(occasionId);
      setSelectedSubOccasionIds(subOccasionIds);
      lastTriggeredGroupIdRef.current = null;
      isLoadingNextPageRef.current = false;
    },
    [selectedOccasionId, selectedSubOccasionIds],
  );

  const hasActiveOccasionFilter = selectedOccasionId !== null || selectedSubOccasionIds.length > 0;

  return (
    <div
      className="min-h-full bg-background"
      style={
        {
          '--look-groups-toolbar-height': '0rem',
          '--look-group-header-height': '3.5rem',
          '--look-group-row-top':
            'calc(var(--look-groups-toolbar-height) + var(--look-group-header-height) + 0.75rem)',
        } as CSSProperties
      }
    >
      <div className="space-y-3 pb-4">
        <div className="bg-background px-0">
          <LookGroupsToolbar
            occasions={occasionTree}
            selectedOccasionId={selectedOccasionId}
            selectedSubOccasionIds={selectedSubOccasionIds}
            isFiltering={isFilterPending}
            onOccasionFilterChange={handleOccasionFilterChange}
          />
        </div>

        {isLoading ? (
          <div className="space-y-3 px-3">
            <Skeleton className="h-160 w-full rounded-sm" />
            <Skeleton className="h-160 w-full rounded-sm" />
          </div>
        ) : lookGroups.length === 0 ? (
          <div className="mx-3 rounded-sm border border-dashed border-border bg-muted/10 px-6 py-12 text-center">
            <p className="m-0 text-base font-semibold text-foreground">No look groups found</p>
            <p className="mt-2 mb-0 text-sm text-muted-foreground">
              {hasActiveOccasionFilter
                ? 'Try a different occasion filter.'
                : 'No published look groups are available right now.'}
            </p>
          </div>
        ) : (
          <div className={isFilterPending ? 'space-y-4 px-3 opacity-60' : 'space-y-4 px-3'}>
            {lookGroups.map((group, index) => (
              <LookGroupWorkspace
                key={`${group.id}:${group.updated_on ?? ''}:${group.looks.length}`}
                summaryGroup={group}
                group={group}
                elementRef={
                  index === lookGroups.length - 1 && hasNextPage ? lastItemRef : undefined
                }
              />
            ))}

            {isFetchingNextPage ? (
              <div className="space-y-3 pt-1">
                <Skeleton className="h-160 w-full rounded-sm" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default LookGroups;
